import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, RefreshCw, Rocket, CheckCircle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface ThankYouPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const ThankYouPopup = ({ isOpen, onClose }: ThankYouPopupProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const handleVisitDashboard = () => {
    onClose();
    navigate("/client-dashboard");
  };

  const handleCloseAndReset = () => {
    // Reset all form data in localStorage
    localStorage.removeItem('formData');
    localStorage.removeItem('devices');
    localStorage.removeItem('duration');
    localStorage.removeItem('selectedLocations');
    localStorage.removeItem('creativeUrls');
    localStorage.removeItem('startDate');
    localStorage.removeItem('endDate');
    localStorage.removeItem('locationCreatives');
    localStorage.removeItem('mediaOwnerCreatives');
    localStorage.removeItem('campaignName');
    localStorage.removeItem('useLocationSpecificCreatives');
    localStorage.removeItem('useMediaOwnerSpecificCreatives');
    localStorage.removeItem('creatives'); // Add this to clear creatives
    
    // Reset to step 1 (index 0)
    localStorage.setItem('currentStep', '0');
    
    // Force reload the page to completely reset the form state
    window.location.reload();
    
    // Close the dialog
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      // Only allow programmatic closing, prevent closing by clicking outside
      if (open === false) {
        // Do nothing when attempting to close by clicking outside
        return;
      }
    }}>
      <DialogContent className="sm:max-w-md overflow-hidden border-none rounded-xl shadow-2xl bg-card dark:bg-card" onPointerDownOutside={(e) => e.preventDefault()}>
        {/* Top gradient bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-light to-primary"></div>
        
        {/* Main content container */}
        <div className="pt-8 pb-6 px-6">
          {/* Success icon - removed the circle wrapper */}
          <div className="flex justify-center mb-6">
            <CheckCircle className="h-14 w-14 text-primary animate-float" />
          </div>
          
          {/* Title and description */}
          <div className="text-center space-y-2">
            <DialogTitle className="text-2xl font-bold text-foreground">
              {t('campaignCreated') || "Campaign Created Successfully!"}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground max-w-xs mx-auto">
              {t('campaignSubmittedDescription') || "Your campaign has been successfully submitted and is now being processed by our team for review."}
            </DialogDescription>
          </div>
        </div>
        
        {/* Decorative wave divider */}
        <div className="relative w-full h-1 overflow-hidden">
          <svg className="w-full h-8 text-primary/10" viewBox="0 0 1200 30" preserveAspectRatio="none">
            <path 
              d="M0,0 C300,30 600,0 900,20 L1200,0 L1200,30 L0,30 Z" 
              fill="currentColor"
            />
          </svg>
        </div>
        
        {/* Actions section */}
        <div className="p-6 pt-4 bg-muted/50 dark:bg-muted/20">
          {/* Information box */}
          <div className="mb-5 p-4 rounded-lg bg-primary/5 border border-primary/10">
            <p className="flex items-center justify-center gap-2 text-muted-foreground font-medium">
              <Rocket className="h-4 w-4 text-primary" />
              {t('campaignViewInstructions') || "Get insights and analytics for your campaign in our exclusive client dashboard"}
            </p>
          </div>
          
          {/* Action buttons */}
          <div className="space-y-3">
            <Button 
              className="w-full"
              onClick={handleVisitDashboard}
            >
              {t('visitClientPortal') || "Access Dashboard"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full group bg-card dark:bg-card hover:bg-muted"
              onClick={handleCloseAndReset}
            >
              <RefreshCw className="mr-2 h-4 w-4 group-hover:rotate-180 transition-transform duration-500" />
              {t('closeAndStayHere') || "Start Another Campaign"}
            </Button>
          </div>
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary/5 rounded-full blur-2xl"></div>
        <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-primary/5 rounded-full blur-2xl"></div>
      </DialogContent>
    </Dialog>
  );
};

export default ThankYouPopup;
